import { TabContext, TabPanel } from "@mui/lab";
import { Box, Divider, Typography, IconButton } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import * as React from "react";
import withLayout from "../../../hocs/Layout";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { useGetChannelOrderQuery, useGetArchivedChannelOrdersQuery } from "../../../redux/commonRTK";
import UnfulfilledChannelOrders from "./UnfulfilledChannelOrders";
import PageTitle from "../../../common/PageTitle";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import FulfilledOutsideChannelOrders from "./fullfilledOutside";
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import ChannelOrderAnalytics from "./ChannelOrderAnalytics";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChannelOrderHelp from "./channelOrderHelp";
import FlightIcon from '@mui/icons-material/Flight';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import WarningIcon from '@mui/icons-material/Warning';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RTSChannelOrders from "./RTSChannelOrders";
import ManifestedChannelOrders from "./ManifestedChannelOrders";
import OtherChannelOrders from "./OtherChannelOrders";
import CancelIcon from '@mui/icons-material/Cancel';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ArchivedChannelOrders from "./ArchivedChannelOrders";
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, INFO, PURPLE, withOpacity } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { pageContainerSx, tabContainerSx, tabPanelSx } from '../../../styles/patterns';

// Status groups per tab — MUST mirror the filters each TabPanel's list component
// applies, so the segment counts match the rows the user sees inside the tab.
const IN_TRANSIT_STATUSES = ["In Transit", "Shipment Is being held", "Shipment Out For Delivery", "Contact To The Customer Care", "Shipment Is Delayed Or Misroute",
  "Delivery Failed", "Out For Delivery"];
const RTO_STATUSES = ["Shipment Is Out For Delivery For RTO", "Marked As Return", "RTO Delivered", "RTO Shipment Delayed", "Rto For Shipment has been Requested", "RTO In Transit"];
const PRE_PICKUP_STATUSES = [
  "Order Has Been Placed / Manifested on Courier Partner",
  "AWB_REGISTERED",
  "AWB registered on proship",
  "Pickup Pending",
  "Shipment Out For Pickup",
  "Pickup Failed",
];

function ChannelOrders() {
  const merchant = getMerchant();
  const merchantId = merchant.id ? merchant.id : ''

  // Per-tab count queries (limit 1 / includes id → count only). Filters mirror
  // each tab's list component so numbers match the tab contents.
  // countOnly short-circuits the backend right after countDocuments — without it
  // each of the nine tiles below also runs a find it never uses.
  const countQuery = (andfilter) => ({ limit: 1, offset: 0, includes: "id", countOnly: true, andfilter });
  const merchantFilter = { field: "merchant.$id", operator: "eq", value: merchantId };

  const { data: newOrders, refetch: refetchNewOrders } = useGetChannelOrderQuery(countQuery([
    { field: "channelOrderStatus", operator: "in", value: ['CREATED', 'HOLD', 'DELETED', 'RELEASE', 'PARTIALLY_FULLFILLED'] }, merchantFilter,
  ]));
  const { data: rtsOrders } = useGetChannelOrderQuery(countQuery([
    { field: "proshipOrder.currentStatus", operator: "in", value: PRE_PICKUP_STATUSES }, merchantFilter,
    { field: "proshipOrder.manifestNumber", operator: "exists", value: false },
  ]));
  const { data: manifestedOrders } = useGetChannelOrderQuery(countQuery([
    { field: "proshipOrder.currentStatus", operator: "in", value: PRE_PICKUP_STATUSES }, merchantFilter,
    { field: "proshipOrder.manifestNumber", operator: "exists", value: true },
  ]));
  const { data: pickedUpOrders } = useGetChannelOrderQuery(countQuery([
    { field: "proshipOrder.currentStatus", operator: "in", value: ["Pickup Has Been Done"] }, merchantFilter,
  ]));
  const { data: inTransitOrders } = useGetChannelOrderQuery(countQuery([
    { field: "proshipOrder.currentStatus", operator: "in", value: IN_TRANSIT_STATUSES }, merchantFilter,
  ]));
  const { data: deliveredOrders } = useGetChannelOrderQuery(countQuery([
    { field: "proshipOrder.currentStatus", operator: "in", value: ["Shipment Delivered"] }, merchantFilter,
  ]));
  const { data: rtoOrders } = useGetChannelOrderQuery(countQuery([
    { field: "proshipOrder.currentStatus", operator: "in", value: RTO_STATUSES }, merchantFilter,
  ]));
  const { data: cancelledOrders } = useGetChannelOrderQuery(countQuery([
    { field: "channelOrderStatus", operator: "eq", value: "CANCELLED" }, merchantFilter,
  ]));
  const { data: fulfilledOutsideOrders } = useGetChannelOrderQuery(countQuery([
    { field: "channelOrderStatus", operator: "eq", value: "FULLFILLED_OUTSIDE" }, merchantFilter,
  ]));

  // Archived orders are stored apart from live orders and wrapped, so they have their own count query.
  const { data: archivedOrders, refetch: refetchArchivedOrders } = useGetArchivedChannelOrdersQuery({
    limit: 1, offset: 0, countOnly: true,
    andfilter: [{ field: "channelOrder.merchant.$id", operator: "eq", value: merchantId }],
  });
  // Archiving moves orders between the New and Archived tabs, so refresh both counts.
  const onOrdersArchived = () => { refetchNewOrders(); refetchArchivedOrders(); };

  const [value, setValue] = React.useState("1");
  const [showHelp, setShowHelp] = React.useState(false);

  // Scroll arrows for the tab strip — shown only when there's actually more to scroll
  // to on that side, since the native scrollbar is hidden.
  const tabScrollRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateScrollArrows = React.useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  React.useEffect(() => {
    updateScrollArrows();
    window.addEventListener('resize', updateScrollArrows);
    return () => window.removeEventListener('resize', updateScrollArrows);
  }, [updateScrollArrows]);

  const scrollTabsBy = (amount) => {
    tabScrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  // Segment-style status tabs — same design as the D2C Forward Shipments page.
  const tabs = [
    { label: 'New', icon: <PendingActionsIcon />, count: newOrders?.count, color: PRIMARY_BLUE },
    { label: 'Ready to Ship', icon: <LocalShippingOutlinedIcon />, count: rtsOrders?.count, color: '#ff9800' },
    { label: 'Manifested', icon: <InventoryIcon />, count: manifestedOrders?.count, color: PURPLE },
    { label: 'Picked Up', icon: <LocalShippingIcon />, count: pickedUpOrders?.count, color: INFO },
    { label: 'In Transit', icon: <FlightIcon />, count: inTransitOrders?.count, color: '#ff9800' },
    { label: 'Delivered', icon: <CheckBoxIcon />, count: deliveredOrders?.count, color: SUCCESS },
    { label: 'RTO', icon: <WarningIcon />, count: rtoOrders?.count, color: ERROR },
    { label: 'Cancelled', icon: <CancelIcon />, count: cancelledOrders?.count, color: ERROR },
    { label: 'Fulfilled Outside', icon: <DirectionsBikeIcon />, count: fulfilledOutsideOrders?.count, color: SUBTLE },
    { label: 'Archived', icon: <ArchiveOutlinedIcon />, count: archivedOrders?.count, color: SUBTLE },
    { label: 'Analytics', icon: <AnalyticsOutlinedIcon />, count: null, color: NAVY },
  ];

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Manage orders synced from your connected sales channels">Channel Orders</PageTitle>

      <TabContext value={value}>
        <Box sx={tabContainerSx}>
          {/* Status tabs — segmented card, mirrors D2C Forward Shipments */}
          <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden', position: 'relative' }}>
            {canScrollLeft && (
              <IconButton
                onClick={() => scrollTabsBy(-220)}
                size="small"
                sx={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 2, borderRadius: 0,
                  px: 0.5, bgcolor: WHITE,
                  background: `linear-gradient(to right, ${WHITE} 60%, ${withOpacity(WHITE, 0)})`,
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                <ChevronLeftIcon sx={{ color: SUBTLE }} />
              </IconButton>
            )}
            {canScrollRight && (
              <IconButton
                onClick={() => scrollTabsBy(220)}
                size="small"
                sx={{
                  position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 2, borderRadius: 0,
                  px: 0.5, bgcolor: WHITE,
                  background: `linear-gradient(to left, ${WHITE} 60%, ${withOpacity(WHITE, 0)})`,
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                <ChevronRightIcon sx={{ color: SUBTLE }} />
              </IconButton>
            )}
            <Box
              ref={tabScrollRef}
              onScroll={updateScrollArrows}
              sx={{
              display: 'flex', overflowX: 'auto',
              // Hide the native scrollbar — it renders raw and gets clipped by the
              // rounded corners of the parent card. Scroll stays fully usable via
              // trackpad/touch/shift+wheel, and the arrow buttons above cover clicking.
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}>
              {tabs.map((t, i) => {
                const tabValue = String(i + 1);
                const isActive = value === tabValue;
                const countDisplay = t.count == null ? '' : t.count > 99999 ? '99,999+' : t.count?.toLocaleString?.() ?? '—';
                return (
                  <React.Fragment key={t.label}>
                    {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />}
                    <Box
                      onClick={() => setValue(tabValue)}
                      sx={{
                        // fixed minWidth (matches D2C Forward Shipments) keeps every
                        // segment the same size regardless of label length; bar scrolls
                        // when it can't fit rather than shrinking tabs unevenly.
                        flex: '1 1 auto', minWidth: 170, py: 1.25, px: 2, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                        borderBottom: isActive ? `3px solid ${t.color}` : '3px solid transparent',
                        bgcolor: isActive ? withOpacity(t.color, 0.06) : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: withOpacity(t.color, 0.04) },
                      }}
                    >
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                        bgcolor: isActive ? withOpacity(t.color, 0.12) : withOpacity(t.color, 0.07),
                        color: isActive ? t.color : SUBTLE,
                        '& .MuiSvgIcon-root': { fontSize: 17 },
                      }}>
                        {t.icon}
                      </Box>
                      <Box>
                        <Typography sx={{
                          fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium,
                          color: isActive ? NAVY : SUBTLE, lineHeight: 1.2, whiteSpace: 'nowrap',
                        }}>
                          {t.label}
                        </Typography>
                        {t.count != null && (
                          <Typography sx={{
                            fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold,
                            color: isActive ? t.color : NAVY, lineHeight: 1.3,
                          }}>
                            {countDisplay}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
            <Typography component="a" onClick={() => setShowHelp(true)}
              sx={{
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                color: PRIMARY_BLUE, textDecoration: 'none', fontFamily: FONT, fontSize: SIZE['2xs'],
                '&:hover': { textDecoration: 'underline' },
              }}>
              <HelpOutlineIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Store Configuration Guide
            </Typography>
          </Box>

          <ChannelOrderHelp open={showHelp} onClose={() => setShowHelp(false)} />
          <TabPanel value="1" sx={tabPanelSx}><UnfulfilledChannelOrders onArchived={onOrdersArchived} /></TabPanel>
          <TabPanel value="2" sx={tabPanelSx}><RTSChannelOrders /></TabPanel>
          <TabPanel value="3" sx={tabPanelSx}><ManifestedChannelOrders /></TabPanel>
          <TabPanel value="4" sx={tabPanelSx}><OtherChannelOrders status={["Pickup Has Been Done"]} /></TabPanel>
          <TabPanel value="5" sx={tabPanelSx}>
            <OtherChannelOrders status={IN_TRANSIT_STATUSES} />
          </TabPanel>
          <TabPanel value="6" sx={tabPanelSx}><OtherChannelOrders status={["Shipment Delivered"]} /></TabPanel>
          <TabPanel value="7" sx={tabPanelSx}>
            <OtherChannelOrders status={RTO_STATUSES} />
          </TabPanel>
          <TabPanel value="8" sx={tabPanelSx}><UnfulfilledChannelOrders canceled={true} /></TabPanel>
          <TabPanel value="9" sx={tabPanelSx}><FulfilledOutsideChannelOrders /></TabPanel>
          <TabPanel value="10" sx={tabPanelSx}><ArchivedChannelOrders /></TabPanel>
          <TabPanel value="11" sx={tabPanelSx}><ChannelOrderAnalytics merchantId={merchantId} /></TabPanel>
        </Box>
      </TabContext>
    </Box>
  );
}

export default withLayout(ChannelOrders);
